import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { API_BASE_URL } from "../config";

const TOKEN_STORAGE_KEY = "tokendata"; // keep existing key used across the app

function safeJsonParse(value) {
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

const initialTokenData = safeJsonParse(localStorage.getItem(TOKEN_STORAGE_KEY));

function extractAccessToken(tokenData) {
  return (
    tokenData?.token ||
    tokenData?.accessToken ||
    tokenData?.data?.accessToken ||
    null
  );
}

function normalizeTokenData(apiResponse) {
  // Supports both older `{ token }` shape and new shape:
  // { statusCode, data: { user, accessToken, refreshToken }, ... }
  const accessToken =
    apiResponse?.data?.accessToken || apiResponse?.accessToken || apiResponse?.token || null;
  const refreshToken =
    apiResponse?.data?.refreshToken || apiResponse?.refreshToken || null;
  const user = apiResponse?.data?.user || apiResponse?.user || null;

  return {
    ...apiResponse,
    token: accessToken, // keep compatibility with components expecting `tokendata.token`
    accessToken,
    refreshToken,
    user,
    role: user?.role || apiResponse?.role || null,
  };
}

export const login = createAsyncThunk(
  "auth/login",
  /**
   * @param {{ email?: string, username?: string, password: string } | any} credentials
   */
  async (credentials, { rejectWithValue }) => {
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/v1/user/login`,
        {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          body: JSON.stringify(credentials ?? {}),
        }
      );

      const text = await res.text();
      const data = text ? safeJsonParse(text) : null;

      if (!res.ok) {
        // Try to surface a helpful message from common API shapes
        const message =
          data?.message ||
          data?.error ||
          data?.msg ||
          `Login failed (${res.status})`;
        return rejectWithValue({ status: res.status, message, data });
      }

      const normalized = normalizeTokenData(data);
      localStorage.setItem(TOKEN_STORAGE_KEY, JSON.stringify(normalized));

      return normalized;
    } catch (err) {
      return rejectWithValue({
        status: 0,
        message: err?.message || "Network error",
      });
    }
  }
);

const authSlice = createSlice({
  name: "auth",
  initialState: {
    tokenData: initialTokenData,
    isAuthenticated: Boolean(extractAccessToken(initialTokenData)),
    loading: false,
    error: null,
  },
  reducers: {
    hydrateFromStorage(state) {
      const tokenData = safeJsonParse(localStorage.getItem(TOKEN_STORAGE_KEY));
      state.tokenData = tokenData;
      state.isAuthenticated = Boolean(extractAccessToken(tokenData));
    },
    logout(state) {
      localStorage.removeItem(TOKEN_STORAGE_KEY);
      state.tokenData = null;
      state.isAuthenticated = false;
      state.loading = false;
      state.error = null;
    },
    clearAuthError(state) {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(login.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.loading = false;
        state.tokenData = action.payload;
        state.isAuthenticated = Boolean(extractAccessToken(action.payload));
      })
      .addCase(login.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || action.error?.message || "Login failed";
      });
  },
});

export const { hydrateFromStorage, logout, clearAuthError } = authSlice.actions;

export const selectAuth = (state) => state.auth;
export const selectTokenData = (state) => state.auth.tokenData;
export const selectAuthToken = (state) => extractAccessToken(state.auth.tokenData);
export const selectAuthLoading = (state) => state.auth.loading;
export const selectAuthError = (state) => state.auth.error;
export const selectIsAuthenticated = (state) => state.auth.isAuthenticated;
export const selectUser = (state) => state.auth.tokenData?.user || null;

export default authSlice.reducer;


